import React from 'react';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/StorefrontOutlined';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import Box from '@mui/material/Box';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { DeliveryState, Order, Shipment } from '../../../../../../Models';

// 'requested' must be represented by a search Icon
// 'on_route_to_pickup': must not be shown as a step, half of the line connecting the steps requested and at_pickup must be painted in blue
// 'at_pickup': must be represented by a hosptial/pharmacy icon
// 'on_route_to_dropoff': must not be shown as a step, half of the line connecting the steps at_pickup and delivered_at_dropoff must be painted in blue
// 'at_dropoff': must be represented by a house icon
// 'delivered_at_dropoff': must not be shown as a step. at_dropoff icon must be shown as green
// if order.trip_type === "round_trip", add a second row of lines connecting the steps. This line progreses in reverse order (right to left).
// 'returning_to_pickup': must not be shown as a step, half of the line connecting at_dropoff and at_pickup must be painted in blue from right to left
// 'returned_to_pickup': must not be shown as a step, the entire line connecting at_dropoff and at_pickup must be painted in blue from right to left
// 'delivered_at_pickup':  must not be shown as a step. at_pickup icon must be shown as green

const STATE_FLOW: { forward: DeliveryState[]; return: DeliveryState[] } = {
    forward: [
        'requested',
        'on_route_to_pickup',
        'at_pickup',
        'on_route_to_dropoff',
        'at_dropoff',
        'delivered_at_dropoff',
    ],
    return: ['returning_to_pickup', 'returned_to_pickup', 'delivered_at_pickup'],
};

const ALL_STATES = [...STATE_FLOW.forward, ...STATE_FLOW.return, 'archived'];

const VISIBLE_STEPS = [
    { state: 'requested', label: 'Solicitado' },
    { state: 'at_pickup', label: 'Farmacia' },
    { state: 'at_dropoff', label: 'Punto de entrega' },
] as const;

const problemStates: DeliveryState[] = [
    // "internal_cancel",
    'incident',
    'returning',
    'returned',
    'courier_canceled',
    'pickup_failed',
];

function ShipmentTimeline({ shipment, order }: { shipment: Shipment | undefined; order: Order }) {
    let currentState: DeliveryState;
    if (shipment?.state) {
        currentState = shipment.state;
    } else if (order.state === 'fulfilled') {
        currentState = 'archived';
    } else {
        currentState = 'estimated';
    }

    const getStateOrder = (state?: DeliveryState): number => {
        if (!state) return 0;
        return ALL_STATES.indexOf(state);
    };

    const getStepIcon = (label: DeliveryState) => {
        const currentStateOrder = getStateOrder(currentState);
        if (label === 'requested') {
            if (currentState === 'estimated') return <SearchIcon />;
            if (problemStates.includes(currentState)) {
                return <SearchIcon sx={{ color: 'orange' }} />;
            }
            return currentStateOrder > getStateOrder('requested') ? (
                <SearchIcon sx={{ color: 'green' }} />
            ) : (
                <SearchIcon
                    sx={{
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                            '0%': { transform: 'scale(0.9)', opacity: 1 },
                            '50%': { transform: 'scale(1.2)', opacity: 0.7 },
                            '100%': { transform: 'scale(0.9)', opacity: 1 },
                        },
                        color: '#1976d2',
                    }}
                />
            );
        }
        if (label === 'at_pickup') {
            if (problemStates.includes(currentState)) {
                return <StorefrontIcon sx={{ color: 'orange' }} />;
            }
            return currentStateOrder > getStateOrder('at_pickup') ||
                currentState === 'delivered_at_pickup' ? (
                <StorefrontIcon sx={{ color: 'green' }} />
            ) : (
                <StorefrontIcon />
            );
        }
        if (label === 'at_dropoff') {
            if (problemStates.includes(currentState)) {
                return <HomeIcon sx={{ color: 'orange' }} />;
            }
            return currentStateOrder > getStateOrder('at_dropoff') ||
                currentState === 'delivered_at_dropoff' ? (
                <HomeIcon sx={{ color: 'green' }} />
            ) : (
                <HomeIcon />
            );
        }
        return null;
    };

    const isStepActive = (label: DeliveryState, state?: DeliveryState): boolean => {
        if (state && problemStates.includes(state)) {
            return false;
        }
        // Solo marcar como active si es el paso visible más cercano al estado actual
        return (
            (state === 'requested' && label === 'requested') ||
            (state === 'on_route_to_pickup' && label === 'at_pickup') ||
            // (state === 'at_pickup' && label === 'at_pickup') ||
            (state === 'on_route_to_dropoff' && label === 'at_dropoff') ||
            // (state === 'at_dropoff' && label === 'at_dropoff') ||
            (state === 'returning_to_pickup' && label === 'at_dropoff')
        );
    };
    const isStepCompleted = (label: DeliveryState, nowState?: DeliveryState) => {
        const currentIndex = getStateOrder(nowState);
        const stepIndex = getStateOrder(label);
        return (
            currentIndex >= stepIndex ||
            nowState === 'archived' ||
            (nowState && problemStates.includes(nowState))
        );
    };

    // Línea principal (ida → gradiente izquierda→derecha)
    const ForwardConnector = styled(StepConnector)(() => ({
        [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 10 },
        // tramo EN CURSO
        [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
            background: problemStates.includes(currentState)
                ? 'linear-gradient(to right, #ed6c02 50%, #eaeaf0 50%)'
                : 'linear-gradient(to right, #1976d2 50%, #eaeaf0 50%)',
        },
        // tramos COMPLETADOS
        [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
            backgroundColor: problemStates.includes(currentState) ? '#ed6c02' : '#1976d2',
        },
        // tramos pendientes
        [`& .${stepConnectorClasses.line}`]: {
            height: 2,
            border: 0,
            backgroundColor: '#eaeaf0',
            borderRadius: 10,
        },
    }));

    // Línea principal (ida → gradiente izquierda→derecha)
    const BackwardConnector = styled(StepConnector)(() => ({
        [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 10 },
        // tramo EN CURSO
        [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
            background: problemStates.includes(currentState)
                ? 'linear-gradient(to right, #eaeaf0 50%, #ed6c02 50%)'
                : 'linear-gradient(to right, #eaeaf0 50%, #1976d2 50%)',
        },
        // tramos COMPLETADOS
        [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
            backgroundColor: problemStates.includes(currentState) ? '#ed6c02' : '#1976d2',
        },
        // tramos pendientes
        [`& .${stepConnectorClasses.line}`]: {
            height: 2,
            border: 0,
            backgroundColor: '#eaeaf0',
            borderRadius: 10,
        },
    }));
    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Box width="100%">
                <Stepper
                    alternativeLabel
                    connector={
                        STATE_FLOW.forward.includes(currentState) ? (
                            <ForwardConnector />
                        ) : (
                            <BackwardConnector />
                        )
                    }>
                    {VISIBLE_STEPS.map(({ state, label }) => (
                        <Step
                            key={label}
                            completed={isStepCompleted(state, currentState)}
                            active={isStepActive(state, currentState)}>
                            <StepLabel icon={getStepIcon(state)}>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>
        </Stack>
    );
}

export default ShipmentTimeline;
